import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  GET_FORM,
  CLEAR_FORM,
} from '@control-front-end/common/constants/forms';
import {
  Modal,
  ModalButtons,
  ModalContent,
  TextField,
  Button,
  Upload,
  Avatar,
  ProgressBar,
} from 'mw-style-react';
import cn from 'classnames';
import Form from '@control-front-end/forms';
import { CREATE_ACTOR } from '@control-front-end/common/constants/graphActors';
import { DEL_MODAL, SHOW_NOTIFY, NOTIFY_LEVEL, UPLOAD_FILE } from 'constants';
import AppUtils from '@control-front-end/utils/utils';
import SelectActorsTemplate from '@control-front-end/common/components/SelectActorsTemplate';
import useIntl from 'useIntl';
import mes from './intl';
// eslint-disable-next-line no-unused-vars
import sModal from '../../Modal.scss'; // NOSONAR
// eslint-disable-next-line no-unused-vars
import sLocal from './CreateActor.scss'; // NOSONAR

function CreateActor(props) {
  const {
    data: { form, onSubmitForm },
    visibility,
    onContentChange,
    onClose,
    callback,
  } = props;
  const t = useIntl();
  const formRef = useRef();
  const dispatch = useDispatch();
  const formView = useSelector((state) => state.formView);
  const { sections } = formView.form;
  const defaultTpl = useSelector((state) => state.defaultActorTpl);
  const [isLoading, setLoading] = useState(false);
  const [title, setTitle] = useState(props.data.title || '');
  const [description, setDescription] = useState(props.data.description || '');
  const [picture, setPicture] = useState(props.data.picture || null);
  const [pictureUrl, setPictureUrl] = useState(props.data.pictureUrl || '');
  const [template, setTemplate] = useState(
    form || { id: defaultTpl.id, title: defaultTpl.title }
  );

  useEffect(() => {
    if (!template.id) return;
    setLoading(true);
    dispatch({
      type: GET_FORM.REQUEST,
      payload: { formId: template.id, withRelations: true },
      afterReq: () => setLoading(false),
    });
    return () => {
      dispatch({ type: CLEAR_FORM });
    };
  }, [template]);

  const onSubmit = () => {
    const { id, areaPicture, pictureObject, position, onSaveActor } =
      props.data;
    const formData = !formView.isDefault ? formRef.current.getFormData() : {};
    if (!formData) return;
    const actorData = {
      title,
      description,
      picture,
      areaPicture,
      pictureObject,
      position,
    };
    if (id && onSaveActor) {
      onSaveActor(template.id, actorData, formData);
    } else {
      dispatch({
        type: CREATE_ACTOR.REQUEST,
        payload: {
          ...actorData,
          formId: template.id,
          formData,
        },
        callback: (actor) => {
          dispatch({
            type: SHOW_NOTIFY.REQUEST,
            payload: {
              id: AppUtils.createRid(),
              type: NOTIFY_LEVEL.SUCCESS,
              label: t(mes.actorCreated),
            },
          });
          if (callback) callback(actor);
        },
      });
    }
    dispatch({ type: DEL_MODAL, payload: 'CreateActor' });
  };

  /**
   *  Изменить изображение для актора
   */
  const managePicture = (newPicture = '') => {
    const newPictureUrl = newPicture
      ? AppUtils.makeAppUrl(`/download/${newPicture}`)
      : '';
    setPicture(newPicture);
    setPictureUrl(newPictureUrl);
  };

  /**
   * Загрузить изображение для актора
   */
  const handleUploadPicture = ({ value }) => {
    dispatch({
      type: UPLOAD_FILE.REQUEST,
      payload: {
        files: value,
        callback: (attachments) => managePicture(attachments[0].fileName),
      },
    });
  };

  const renderUploadAvatar = () => {
    return (
      <div
        styleName={cn('sLocal.content__upload', {
          'sLocal.update': !!pictureUrl,
        })}
      >
        <Upload type="avatar" accept="image/*" onChange={handleUploadPicture}>
          <Avatar
            styleName="sLocal.avatar"
            size="xlarge"
            src={pictureUrl || ''}
          />
          <Button
            type="text"
            fontWeight="normal"
            label={pictureUrl ? t(mes.changeImage) : t(mes.uploadImage)}
          />
        </Upload>
        {pictureUrl ? (
          <Button
            type="text"
            fontWeight="normal"
            label={t(mes.delImage)}
            onClick={() => managePicture('')}
          />
        ) : null}
      </div>
    );
  };

  const renderLoader = () => (
    <div styleName="sLocal.content__loader">
      <ProgressBar type="circle" size="large" />
    </div>
  );

  const defaultActorFields = (
    <div styleName="sLocal.default">
      <SelectActorsTemplate
        bordered={true}
        label={t(mes.actorTpl)}
        placeholder={t(mes.selectFromList)}
        value={template}
        onChange={({ value }) => setTemplate(value)}
      />
      {renderUploadAvatar()}
      <TextField
        styleName="sLocal.row__field"
        autoFocus={!title}
        bordered={true}
        label={t(mes.title)}
        value={title}
        onChange={({ value }) => {
          setTitle(value);
          onContentChange();
        }}
      />
      <TextField
        styleName="sLocal.row__field"
        bordered={true}
        label={t(mes.description)}
        value={description}
        onChange={({ value }) => {
          setDescription(value);
          onContentChange();
        }}
      />
    </div>
  );

  let modalTitle;
  if (props.data.id) {
    modalTitle = `${t(mes.updateTplActor)} '${props.data.title}'`;
  } else {
    modalTitle = `${t(mes.createTplActor)} (${template.title})`;
  }

  return (
    <Modal
      styleName="sModal.modal sLocal.modal"
      visibility={visibility}
      onClose={onClose}
      label={modalTitle}
      size="xlarge"
    >
      <ModalContent styleName="sModal.modal__content sLocal.content">
        {sections ? (
          <Form
            ref={formRef}
            key={template.id}
            formId={template.id}
            type="modal"
            form={
              props.data.id && template.id === form.id ? form : formView.form
            }
            onChange={onContentChange}
            onSubmit={onSubmitForm}
            extraSection={defaultActorFields}
          />
        ) : null}
        {isLoading ? renderLoader() : null}
      </ModalContent>
      <ModalButtons styleName="sModal.modal__buttons">
        <Button label={t(mes.save)} size="large" onClick={onSubmit} />
      </ModalButtons>
    </Modal>
  );
}

CreateActor.propTypes = {
  visibility: PropTypes.bool.isRequired,
  data: PropTypes.object,
  onClose: PropTypes.func,
  callback: PropTypes.func,
  onContentChange: PropTypes.func,
};

export default CreateActor;
